import { useMemo, useState } from "react"
import { Check, RotateCcw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"
import { useUpdateUsinageWaste } from "@/features/milling/hooks/use-milling-mutations"
import { buildRapport } from "@/features/milling/lib/rapport"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

interface FreintePanelProps {
  usinage: UsinageDetail
  loads: CouplingLoadView[]
}

// On-screen Freinte rapport — per-planned-lot reconciliation of
// engaged (planned) weight vs obtained (actual) weight. The summary
// below collapses every planned lot into a single Ecart + Freinte%
// and folds in the operator-reported milling waste (déchet).
//
// Pending planned lots render their actual/ecart columns as dashes
// (the operator hasn't milled them yet). The summary still computes
// against the running totals, so it stays meaningful mid-fabrication.
//
// The déchet field is editable inline (one value per usinage); on
// save it flows through the cached usinage detail so the KPI strip
// and this panel refresh in one round-trip.
export function FreintePanel({ usinage, loads }: FreintePanelProps) {
  const { t } = useTranslation("milling")
  const data = useMemo(() => buildRapport(usinage, loads), [usinage, loads])
  const { rows, summary } = data.freinte

  // The persisted déchet value (kg — same unit as the operator's
  // input). The WasteEditor is keyed by it so a successful save —
  // which mutates the cache and changes this prop — re-mounts the
  // editor with the fresh backend value, sidestepping the
  // useEffect-driven sync that the react-hooks/set-state-in-effect
  // lint rule rejects.
  const persistedKg = usinage.wasteKg ?? 0

  return (
    <Card className="flex flex-col gap-0 overflow-hidden p-0">
      <div className="border-b bg-muted/40 px-4 py-3">
        <h3 className="text-sm font-semibold tracking-tight">
          {t("rapport.freinteHeading")}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t("rapport.freinteSubtitle")}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/20 text-muted-foreground">
              <th className={HEAD_CELL}>
                {t("rapport.freinteCols.plannedLot")}
              </th>
              <th className={HEAD_CELL}>
                {t("rapport.freinteCols.lotNumber")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("rapport.freinteCols.plannedKg")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("rapport.freinteCols.actualKg")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("rapport.freinteCols.ecartKg")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="py-6 text-center text-xs text-muted-foreground"
                >
                  {t("rapport.freinteEmpty")}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.plannedLotId}>
                  <td className={cn(BODY_CELL, "font-medium")}>
                    {r.plannedLabel}
                  </td>
                  <td className={cn(BODY_CELL, "font-mono text-[11px]")}>
                    {r.fabricatedLotNumber ?? (
                      <span className="text-muted-foreground/50">
                        {t("rapport.freintePending")}
                      </span>
                    )}
                  </td>
                  <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                    {formatNumber(r.plannedKg, 0)}
                  </td>
                  <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                    {r.actualKg != null ? (
                      formatNumber(r.actualKg, 0)
                    ) : (
                      <span className="text-muted-foreground/50">
                        {t("rapport.freintePending")}
                      </span>
                    )}
                  </td>
                  <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                    {r.ecartKg != null ? (
                      formatNumber(r.ecartKg, 0)
                    ) : (
                      <span className="text-muted-foreground/50">
                        {t("rapport.freintePending")}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <dl className="grid grid-cols-2 gap-px border-t bg-border sm:grid-cols-5">
        <SummaryTile
          label={t("rapport.freinteSummary.totalPlanned")}
          value={`${formatNumber(summary.totalPlannedKg, 0)} kg`}
        />
        <SummaryTile
          label={t("rapport.freinteSummary.totalActual")}
          value={`${formatNumber(summary.totalActualKg, 0)} kg`}
        />
        <WasteEditor
          key={persistedKg}
          usinageId={usinage.id}
          persistedKg={persistedKg}
        />
        <SummaryTile
          label={t("rapport.freinteSummary.ecart")}
          value={`${formatNumber(summary.ecartKg, 0)} kg`}
          sub={t("rapport.freinteSummary.ecartFormula")}
        />
        <SummaryTile
          label={t("rapport.freinteSummary.freinte")}
          value={`${formatNumber(summary.freintePct, 2)} %`}
          emphasised
        />
      </dl>
    </Card>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  emphasised,
}: {
  label: string
  value: string
  sub?: string
  emphasised?: boolean
}) {
  return (
    <div className="flex flex-col gap-0.5 bg-card px-4 py-3">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "text-sm font-semibold tabular-nums",
          emphasised && "text-emerald-700 dark:text-emerald-400",
        )}
      >
        {value}
      </dd>
      {sub ? (
        <span className="text-[10px] text-muted-foreground">{sub}</span>
      ) : null}
    </div>
  )
}

// Inline editor for the run-wide déchet. Hosted as its own component
// so the parent can re-mount it via `key={persistedKg}` once a save
// completes — that resets the local input buffer to the fresh backend
// value without needing a useEffect, which the lint rules forbid for
// prop→state sync.
//
// UX:
// - Operator enters kg (the unit the floor scales report in).
// - The persisted value is shown bold above the input — same shape
//   as the other summary tiles, so the tile reads as a number first,
//   editor second.
// - Enter submits, Esc reverts to the persisted value; a small reset
//   button does the same with a mouse. The save button only enables
//   when the buffer differs from the persisted value.
// - A footer line displays the equivalent in MT so operators who
//   think in tonnes can sanity-check.
function WasteEditor({
  usinageId,
  persistedKg,
}: {
  usinageId: string | number
  persistedKg: number
}) {
  const { t } = useTranslation("milling")
  const [wasteKg, setWasteKg] = useState<string>(
    persistedKg > 0 ? String(persistedKg) : "",
  )
  const updateWaste = useUpdateUsinageWaste(usinageId)

  const parsed = Number(wasteKg)
  const valid = wasteKg.trim() === "" || (!Number.isNaN(parsed) && parsed >= 0)
  const numericValue = wasteKg.trim() === "" ? 0 : parsed
  const dirty = valid && Math.abs(numericValue - persistedKg) > 1e-9

  const onSave = () => {
    if (!valid || !dirty) return
    updateWaste.mutate({ wasteKg: numericValue })
  }

  const onReset = () => {
    setWasteKg(persistedKg > 0 ? String(persistedKg) : "")
  }

  return (
    <div className="flex flex-col gap-0.5 bg-card px-4 py-3">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {t("rapport.freinteSummary.waste")}
      </dt>
      <dd className="flex items-center gap-1">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step="1"
          value={wasteKg}
          onChange={(e) => setWasteKg(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onSave()
            } else if (e.key === "Escape") {
              e.preventDefault()
              onReset()
            }
          }}
          placeholder="0"
          aria-label={t("rapport.freinteSummary.wasteHint")}
          aria-invalid={!valid}
          className={cn(
            "h-7 w-24 px-2 text-right text-sm font-semibold tabular-nums",
            !valid && "border-destructive focus-visible:ring-destructive/30",
          )}
        />
        <span className="text-[11px] font-medium text-muted-foreground">
          kg
        </span>
        {dirty ? (
          <>
            <Button
              size="sm"
              variant="default"
              className="h-7 w-7 p-0"
              disabled={!valid || updateWaste.isPending}
              onClick={onSave}
              aria-label={t("rapport.freinteSummary.wasteSave")}
              title={t("rapport.freinteSummary.wasteSave")}
            >
              <Check className="size-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground"
              disabled={updateWaste.isPending}
              onClick={onReset}
              aria-label={t("rapport.freinteSummary.wasteReset")}
              title={t("rapport.freinteSummary.wasteReset")}
            >
              <RotateCcw className="size-3.5" />
            </Button>
          </>
        ) : null}
      </dd>
      <span className="text-[10px] tabular-nums text-muted-foreground">
        {wasteKg.trim() === ""
          ? t("rapport.freinteSummary.wasteHint")
          : valid
            ? t("rapport.freinteSummary.wasteEquivalent", {
                mt: formatNumber(kgToMt(numericValue), 3),
              })
            : t("rapport.freinteSummary.wasteInvalid")}
      </span>
    </div>
  )
}

const HEAD_CELL =
  "h-9 px-3 text-left text-[10px] font-medium uppercase tracking-wide"
const BODY_CELL = "px-3 py-2 text-xs"
